import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';

const SECTOR_BENCHMARKS = {
  'Technology': { level1: 65, level2: 45, level3: 25, maturity_score: 58 },
  'Healthcare': { level1: 72, level2: 52, level3: 30, maturity_score: 64 },
  'Education': { level1: 68, level2: 48, level3: 28, maturity_score: 60 },
  'Finance': { level1: 70, level2: 50, level3: 32, maturity_score: 62 },
  'Retail': { level1: 55, level2: 35, level3: 18, maturity_score: 48 },
  'Manufacturing': { level1: 60, level2: 40, level3: 22, maturity_score: 52 },
  'Public Sector': { level1: 75, level2: 55, level3: 35, maturity_score: 68 },
  'Hospitality': { level1: 50, level2: 30, level3: 15, maturity_score: 42 },
  'Transport': { level1: 58, level2: 38, level3: 20, maturity_score: 50 },
  'Default': { level1: 62, level2: 42, level3: 24, maturity_score: 55 },
};

const COLORS = {
  yours: '#0d9488',      // teal-600
  sector: '#94a3b8',     // slate-400
};

export default function SectorBenchmark({ org, latestResult }) {
  if (!org || !latestResult) {
    return (
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-foreground">Sector Benchmark Comparison</h2>
          <p className="text-xs text-muted-foreground mt-0.5">How your maturity scores compare to sector averages</p>
        </div>
        <p className="text-sm text-muted-foreground">Complete your assessment to see sector benchmarks.</p>
      </div>
    );
  }

  const sector = org.sector || 'Default';
  const benchmark = SECTOR_BENCHMARKS[sector] || SECTOR_BENCHMARKS.Default;

  const yourMaturity = latestResult.maturity_score || 0;
  const sectorAvgMaturity = benchmark.maturity_score;
  const diff = yourMaturity - sectorAvgMaturity;

  const comparisonData = [
    { name: 'Overall', yours: yourMaturity, sector: sectorAvgMaturity },
    { name: 'Level 1', yours: Math.round(latestResult.level1_score || 0), sector: benchmark.level1 },
    { name: 'Level 2', yours: Math.round(latestResult.level2_score || 0), sector: benchmark.level2 },
    { name: 'Level 3', yours: Math.round(latestResult.level3_score || 0), sector: benchmark.level3 },
  ];



  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
      <div className="mb-3 sm:mb-4">
        <h2 className="text-xs sm:text-sm font-semibold text-foreground">Sector Benchmark Comparison</h2>
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
          Your scores vs {sector} averages
        </p>
      </div>

      {/* Summary card */}
      <div className={`rounded-lg p-3 sm:p-4 mb-4 ${diff >= 0 ? 'bg-teal-50 border border-teal-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-muted-foreground">Overall Performance</p>
            <p className="text-sm sm:text-base font-bold text-foreground mt-0.5 truncate">
              {diff > 0 ? 'Above' : diff < 0 ? 'Below' : 'At'} Sector Average
            </p>
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div data-decorative-image="true" className="h-64 relative">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={comparisonData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={[0, 100]} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="yours" name="Your Organisation" fill={COLORS.yours} radius={[4, 4, 0, 0]} label={false} />
            <Bar dataKey="sector" name={`${sector} Average`} fill={COLORS.sector} radius={[4, 4, 0, 0]} label={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="text-only-alternative w-full text-left text-sm">
        <caption className="mb-2 text-left font-semibold">Sector benchmark values</caption>
        <thead><tr><th scope="col">Area</th><th scope="col">Your organisation</th><th scope="col">{sector} average</th></tr></thead>
        <tbody>{comparisonData.map((item) => <tr key={item.name}><th scope="row">{item.name}</th><td>{item.yours}%</td><td>{item.sector}%</td></tr>)}</tbody>
      </table>

      {/* Insights */}
      <div className="mt-5 pt-4 border-t border-border">
        <h3 className="text-xs font-semibold text-foreground mb-2">Key Insights</h3>
        <ul className="space-y-1.5">
          {comparisonData.map((item, idx) => {
            const itemDiff = item.yours - item.sector;
            if (itemDiff > 5) {
              return (
                <li key={idx} className="text-xs text-teal-700 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                  <strong>{item.name}:</strong> Performing {itemDiff}% above sector average
                </li>
              );
            }
            if (itemDiff < -5) {
              return (
                <li key={idx} className="text-xs text-amber-700 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <strong>{item.name}:</strong> {Math.abs(itemDiff)}% below sector average — improvement opportunity
                </li>
              );
            }
            return null;
          })}
          {comparisonData.every(item => Math.abs(item.yours - item.sector) <= 5) && (
            <li className="text-xs text-muted-foreground">Your scores are closely aligned with sector averages across all levels.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
